import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import LeadsTable from 'components/elements/LeadsTable';
import SearchInput from 'components/elements/SearchInput';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { Pagination } from 'components/layout/List/Paging';
import DateCreatedOverlayFilter from 'components/widgets/ProjectInsights/OverlayMenu/DateCreatedOverlayFilter';
import DateUpdatedOverlayFilter from 'components/widgets/ProjectInsights/OverlayMenu/DateUpdatedOverlayFilter';
import StatusOverlayFilter from 'components/widgets/ProjectInsights/OverlayMenu/StatusOverlayFilter';
import ToggleButton from 'components/widgets/ProjectInsights/SearchResultsViews/ToggleButton';
import { useMapProjectList } from 'hooks/projectMap/useMapProjectList';
import { usePaginate } from 'hooks/usePaginate';
import { useAddressSearch } from 'state/map';
import { ProjectGroupTypes } from 'utils/analytics/constants';
import { formatInteger } from 'utils/format';

const containerStyle = {
  height: '100%',
  width: '100%',
  gap: 'padding_16',
  overflow: 'hidden',
  padding: 'padding_8',
  paddingLeft: 'padding_16',
};

const filterItemsStyle = {
  gap: 'padding_48',
  justfiyContent: 'flex-start',
  alignItems: 'flex-start',
};

const inputStyles = {
  gap: 'padding_8',
  flex: '1',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
};

const addressInputStyle = {
  width: '100%',
  maxWidth: '320px',
};

const dropdownFiltersStyle = {
  width: '100%',
  maxWidth: '581px',
  alignItems: 'center',
  gap: 'padding_8',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(0, 188px))',
  justifyContent: 'flex-start',
};

const SearchResultsViews = () => {
  const { projectList } = useMapProjectList({
    useMapFiltersOnly: true,
    allowAddressSearch: true,
  });

  const [addressSearch, setAddressSearch] = useAddressSearch();
  const {
    page: currentPage,
    previousPage,
    nextPage,
    setPageNumber,
    calcTotalPages,
  } = usePaginate();

  const projectsCount = useMemo(() => {
    return projectList.data?.count || 0;
  }, [projectList?.data?.count]);

  const totalPages = useMemo(() => {
    return calcTotalPages(projectsCount) || 1;
  }, [projectsCount]);

  const labelProjectsFound = useMemo(() => {
    if (projectsCount === 0) {
      return '0 projects found';
    }

    return `${formatInteger(projectsCount)} project${
      projectsCount === 1 ? '' : 's'
    } found`;
  }, [projectsCount]);

  return (
    <FlexColumn sx={containerStyle}>
      <FlexRow sx={filterItemsStyle}>
        <FlexRow sx={inputStyles}>
          <Box sx={addressInputStyle}>
            <SearchInput
              placeholder="Search by address"
              value={addressSearch}
              onChange={setAddressSearch}
            />
          </Box>
          <FlexRow sx={dropdownFiltersStyle}>
            <DateCreatedOverlayFilter />
            <DateUpdatedOverlayFilter />
            <StatusOverlayFilter />
          </FlexRow>
        </FlexRow>

        <ToggleButton />
      </FlexRow>

      <LeadsTable
        items={projectList?.data?.projects || []}
        leadContext={{
          groupId: 'project-insights',
          groupType: ProjectGroupTypes.PROJECT_MAP,
        }}
        columnProps={{ showStatus: true }}
        ariaLabel="project insights search results"
        isLoading={projectList.isLoading || projectList.isFetching}
        disableSorting
        header={labelProjectsFound}
        footer={
          <Pagination
            currentPage={currentPage}
            isLoading={projectList.isLoading}
            setPage={(pageNumber) => setPageNumber(pageNumber)}
            totalPages={totalPages}
            onNext={() => nextPage()}
            onPrev={() => previousPage()}
          />
        }
      />
    </FlexColumn>
  );
};

export default SearchResultsViews;
